import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { analytics, getTodaysHours, formatDistance, addCacheBust } from "@/lib/utils";
import { getLocationPath, getLocationDisplayName } from "@/lib/locationUrls";

export default function LocationCard({ location, index, onSignupClick }) {
  const locationPath = getLocationPath(location.id);
  const photos = location.photos || [];
  const hours = location.hours || [];
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
    >
      <div
        className="location-card flex flex-col h-full cursor-pointer"
        data-testid={`location-card-${location.id}`}
        onClick={() => navigate(locationPath)}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(locationPath); }}
      >
        {/* Image */}
        <div className="relative h-48 overflow-hidden rounded-t-[11px]">
          <img
            src={addCacheBust((photos.length > 0 && photos[0]) || "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600")}
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600"; }}
            alt={location.name}
            className="w-full h-full object-cover"
          />
          {location.is_women_only && (
            <div className="absolute top-3 left-3 women-only-badge">
              Women Only
            </div>
          )}
          {location.id === "giant-fitness-mount-ephraim" && (
            <div className="absolute top-3 left-3 giant-fitness-only-badge">
              Giant Fitness Only
            </div>
          )}
          {location.distance !== undefined && (
            <div className="absolute top-3 right-3 bg-black/70 text-white text-sm font-medium px-2 py-1 rounded">
              {formatDistance(location.distance)}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-5 flex flex-col">
          <h3 className="text-xl font-heading font-bold text-gray-900 mb-2">
            {getLocationDisplayName(location.id) ?? location.name}
          </h3>

          <div className="space-y-2 mb-4 flex-1">
            {(location.address || location.city) && (
              <div className="flex items-start gap-2 text-sm text-gray-700">
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  {[location.address, location.city, location.state, location.zip_code]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            )}
            {location.phone && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone className="w-4 h-4" />
                <a
                  href={`tel:${location.phone}`}
                  className="hover:text-brand-orange transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  {location.phone}
                </a>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Clock className="w-4 h-4" />
              <span>
                {hours.length
                  ? `Today: ${getTodaysHours(hours)}`
                  : (location.hours_note || "Hours not available")}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t border-gray-200">
            <Link
              to={locationPath}
              className="flex-1"
              onClick={(e) => { e.stopPropagation(); analytics.trackLocationSelect(location.id, location.name); }}
            >
              <Button
                variant="outline"
                className="w-full rounded-lg border-gray-300 text-gray-700 hover:bg-gray-50"
                data-testid={`view-location-${location.id}`}
              >
                View Details
              </Button>
            </Link>
            {onSignupClick && (
              <div className="flex-1">
                <Button
                  type="button"
                  className="w-full bg-brand-orange hover:bg-brand-orange-hover text-white font-bold"
                  data-testid={`signup-${location.id}`}
                  onClick={(e) => { e.stopPropagation(); onSignupClick(location); }}
                >
                  Sign Up
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
